// Editor section 08 Travel hooks — hand-written over the generated client via the
// tenant-keyed wrappers. Keys are ['band', bandId, 'show', showId, 'travel', …], a child of the show
// key, so the `schedule-changed` signal already invalidates them (no new UI signal). Money-free at the gate
// (schedule:write); travel cost is schedule-tier logistics, its DTO lives out of the money-free net server-side.
//
// PARSE ≠ SAVE: `useParseItinerary` returns structured items for the user to confirm and persists nothing; a
// Gemini outage is a 502 that surfaces on `onError` (the section reveals manual entry — never a silent empty
// state). Only `useSaveTravel` persists. `useFlightStatus` reads the server-poller cache (no client→vendor call).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type TravelView = components["schemas"]["TravelView"];
type SaveTravelRequest = components["schemas"]["SaveTravelRequest"];
type ParseTravelRequest = components["schemas"]["ParseTravelRequest"];
type TravelParseResult = components["schemas"]["TravelParseResult"];
type FlightStatusView = components["schemas"]["FlightStatusView"];

/** GET the show's saved travel legs + assignments. */
export function useTravel(showId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<TravelView>(
    ["show", showId, "travel"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/travel",
        // `enabled: !!showId` gates the query, so the id is present by the time this runs.
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 15_000, enabled: !!showId },
  );
}

/** POST parse — Gemini Vision → structured items for confirm (NOT saved). A 502 (Gemini outage) surfaces on
 * `onError` so the caller reveals manual entry. Does NOT toast — the section renders the degrade inline.
 *
 * `showId` is OPTIONAL, and that is the create flow: a booking agent has the confirmation email before the
 * date is entered anywhere, so with no show yet this posts to the BAND route instead. Same parse, fenced on
 * the band, and the legs come back unassigned — an assignment is validated against the show's own party on
 * save, so there is nobody the show-less form could name that would survive one. */
export function useParseItinerary() {
  const { api } = useApp();
  return useBandMutation<TravelParseResult, { showId?: string; body: ParseTravelRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = showId
        ? await api.POST(
            "/api/v1/bands/{bandId}/shows/{showId}/travel/parse",
            { params: { path: { bandId: band, showId } }, body },
          )
        : await api.POST(
            "/api/v1/bands/{bandId}/travel/parse",
            { params: { path: { bandId: band } }, body },
          );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  );
}

/** PUT save the confirmed items + per-member assignment (full replace). */
export function useSaveTravel() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<TravelView, { showId: string; body: SaveTravelRequest }>(
    async (band, { showId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/shows/{showId}/travel",
        { params: { path: { bandId: band, showId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (_res, vars) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "travel"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save travel")),
    },
  );
}

/** PATCH one travel leg's needs-reimbursement (ART-241 — the editor Expenses section's only editable control
 * on a travel-derived row). Targeted, so it never collides with the travel section's full-replace save. */
export function useSetTravelReimbursement() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { showId: string; travelItemId: string; needsReimbursement: boolean }>(
    async (band, { showId, travelItemId, needsReimbursement }) => {
      const { error, response } = await api.PATCH(
        "/api/v1/bands/{bandId}/shows/{showId}/travel/{travelItemId}/reimbursement",
        { params: { path: { bandId: band, showId, travelItemId } }, body: { needsReimbursement } },
      );
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_r, vars) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "travel"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't update reimbursement")),
    },
  );
}

/** GET flight status from the server-side poller cache (never a client→vendor call). Lazy via `enabled`. */
export function useFlightStatus(showId: string, enabled = false) {
  const { api } = useApp();
  return useBandQuery<FlightStatusView>(
    ["show", showId, "travel", "flight-status"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/shows/{showId}/travel/flight-status",
        // `enabled: !!showId` gates the query, so the id is present by the time this runs.
        { params: { path: { bandId, showId: showId! } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!showId, staleTime: 60_000 },
  );
}
